/* Jubi — Order Status page client.
   Hydrates the server-rendered skeleton with data from the /apps/jubi-order-status
   app proxy. Computes per-rug stage timelines from schedule data.
   No build step — plain browser ES module-style IIFE. */

(function () {
  'use strict';

  const cfg = window.JubiOrderStatus || {};
  const root = document.querySelector('[data-jubi-os]');
  if (!root) return;

  /* ---------- Schedule (ported from assets/schedule.js in the prototype) ---------- */

  const TUFTED_STAGES = [
    { key: 'order_placed',    name: 'Order placed',           days: 1, phase: 'admin'  },
    { key: 'dyeing',          name: 'Wool dyeing',            days: 3, phase: 'craft'  },
    { key: 'tufting',         name: 'Tufting',                days: 13, phase: 'craft'  },
    { key: 'shearing_tufted', name: 'Shearing & carving',     days: 3, phase: 'craft'  },
    { key: 'binding',         name: 'Binding',                days: 2, phase: 'craft'  },
    { key: 'washing',         name: 'Washing & drying',       days: 2, phase: 'finish' },
    { key: 'inspection',      name: 'Quality inspection',     days: 1, phase: 'finish' },
    { key: 'packing',         name: 'Packing',                days: 1, phase: 'finish' },
    { key: 'dispatch_prep',   name: 'Preparing for dispatch', days: 2, phase: 'ship'   },
    { key: 'dispatched',      name: 'Dispatched',             days: 0, phase: 'ship'   },
    { key: 'in_transit',      name: 'In transit',             days: 6, phase: 'ship'   },
    { key: 'delivered',       name: 'Delivered',              days: 0, phase: 'ship'   }
  ];
  const TUFTED_CUSTOM_STAGES = TUFTED_STAGES.map((s) =>
    s.key === 'dyeing' ? { ...s, days: 4 } : s
  );
  const KNOTTED_STAGES = [
    { key: 'order_placed',     name: 'Order placed',           days: 1,  phase: 'admin'  },
    { key: 'dyeing',           name: 'Wool dyeing',            days: 4,  phase: 'craft'  },
    { key: 'looming',          name: 'Dressing the loom',      days: 3,  phase: 'craft'  },
    { key: 'knotting',         name: 'Hand-knotting',          days: 26, phase: 'craft'  },
    { key: 'shearing_knotted', name: 'Shearing',               days: 3,  phase: 'craft'  },
    { key: 'binding',          name: 'Binding',                days: 2,  phase: 'craft'  },
    { key: 'washing',          name: 'Washing & drying',       days: 4,  phase: 'finish' },
    { key: 'inspection',       name: 'Quality inspection',     days: 1,  phase: 'finish' },
    { key: 'packing',          name: 'Packing',                days: 1,  phase: 'finish' },
    { key: 'dispatch_prep',    name: 'Preparing for dispatch', days: 2,  phase: 'ship'   },
    { key: 'dispatched',       name: 'Dispatched',             days: 0,  phase: 'ship'   },
    { key: 'in_transit',       name: 'In transit',             days: 6,  phase: 'ship'   },
    { key: 'delivered',        name: 'Delivered',              days: 0,  phase: 'ship'   }
  ];
  const WOVEN_STAGES = [
    { key: 'order_placed',  name: 'Order placed',           days: 1,  phase: 'admin'  },
    { key: 'dyeing',        name: 'Wool dyeing',            days: 4,  phase: 'craft'  },
    { key: 'looming',       name: 'Dressing the loom',      days: 2,  phase: 'craft'  },
    { key: 'weaving',       name: 'Flatweaving',            days: 30, phase: 'craft'  },
    { key: 'binding',       name: 'Binding',                days: 2,  phase: 'craft'  },
    { key: 'washing',       name: 'Washing & drying',       days: 5,  phase: 'finish' },
    { key: 'inspection',    name: 'Quality inspection',     days: 1,  phase: 'finish' },
    { key: 'packing',       name: 'Packing',                days: 1,  phase: 'finish' },
    { key: 'dispatch_prep', name: 'Preparing for dispatch', days: 2,  phase: 'ship'   },
    { key: 'dispatched',    name: 'Dispatched',             days: 0,  phase: 'ship'   },
    { key: 'in_transit',    name: 'In transit',             days: 6,  phase: 'ship'   },
    { key: 'delivered',     name: 'Delivered',              days: 0,  phase: 'ship'   }
  ];
  const LOOMED_STAGES = [
    { key: 'order_placed',    name: 'Order placed',           days: 1,  phase: 'admin'  },
    { key: 'dyeing',          name: 'Wool dyeing',            days: 4,  phase: 'craft'  },
    { key: 'looming',         name: 'Dressing the loom',      days: 3,  phase: 'craft'  },
    { key: 'hand_looming',    name: 'Hand-looming',           days: 26, phase: 'craft'  },
    { key: 'shearing_loomed', name: 'Shearing',               days: 3,  phase: 'craft'  },
    { key: 'binding',         name: 'Binding',                days: 2,  phase: 'craft'  },
    { key: 'washing',         name: 'Washing & drying',       days: 4,  phase: 'finish' },
    { key: 'inspection',      name: 'Quality inspection',     days: 1,  phase: 'finish' },
    { key: 'packing',         name: 'Packing',                days: 1,  phase: 'finish' },
    { key: 'dispatch_prep',   name: 'Preparing for dispatch', days: 2,  phase: 'ship'   },
    { key: 'dispatched',      name: 'Dispatched',             days: 0,  phase: 'ship'   },
    { key: 'in_transit',      name: 'In transit',             days: 6,  phase: 'ship'   },
    { key: 'delivered',       name: 'Delivered',              days: 0,  phase: 'ship'   }
  ];

  const TECHNIQUE_STAGES = {
    tufted:         TUFTED_STAGES,
    tufted_custom:  TUFTED_CUSTOM_STAGES,
    knotted:        KNOTTED_STAGES,
    woven:          WOVEN_STAGES,
    loomed:         LOOMED_STAGES,
    shag:           KNOTTED_STAGES
  };

  // Non-rug items (blankets, poms, prints, samples) ship from studio stock
  // or short production. No Bhadohi atelier journey.
  const NON_RUG_STAGES = [
    { key: 'order_placed', name: 'Order placed',   days: 1, phase: 'admin'  },
    { key: 'preparing',    name: 'Preparing',      days: 2, phase: 'finish' },
    { key: 'dispatched',   name: 'Dispatched',     days: 0, phase: 'ship'   },
    { key: 'in_transit',   name: 'In transit',     days: 5, phase: 'ship'   },
    { key: 'delivered',    name: 'Delivered',      days: 0, phase: 'ship'   }
  ];
  const TECHNIQUE_LABEL = {
    tufted:         'Hand-tufted',
    tufted_custom:  'Hand-tufted',
    knotted:        'Hand-knotted',
    woven:          'Hand-woven (flatweave)',
    loomed:         'Hand-loomed',
    shag:           'Hand-knotted shag',
    flatweave:      'Hand-woven (flatweave)',
    'hand-tufted':  'Hand-tufted',
    'hand-knotted': 'Hand-knotted',
    'hand-loomed':  'Hand-loomed',
    'hand-woven':   'Hand-woven (flatweave)'
  };

  const TECHNIQUE_ALIASES = {
    tufted: 'tufted',
    'hand-tufted': 'tufted',
    hand_tufted: 'tufted',
    tufted_custom: 'tufted_custom',
    knotted: 'knotted',
    'hand-knotted': 'knotted',
    hand_knotted: 'knotted',
    shag: 'shag',
    'hand-knotted-shag': 'shag',
    hand_knotted_shag: 'shag',
    woven: 'woven',
    'hand-woven': 'woven',
    hand_woven: 'woven',
    flatweave: 'woven',
    loomed: 'loomed',
    'hand-loomed': 'loomed',
    hand_loomed: 'loomed'
  };

  function canonTechnique(tech) {
    if (!tech) return 'tufted';
    const key = String(tech).trim().toLowerCase().replace(/\s+/g, '-');
    return TECHNIQUE_ALIASES[key]
      || TECHNIQUE_ALIASES[key.replace(/-/g, '_')]
      // Unknown technique: preserve the unknown state so the label falls
      // back to "Handmade" instead of claiming "Hand-tufted".
      || 'unknown';
  }
  function labelForTechnique(tech) {
    const canon = canonTechnique(tech);
    return TECHNIQUE_LABEL[canon] || 'Handmade';
  }
  function stagesForTechnique(tech) {
    const canon = canonTechnique(tech);
    // Unknown techniques still need a reasonable production timeline;
    // tufted is the shortest/safest default.
    return TECHNIQUE_STAGES[canon] || TECHNIQUE_STAGES.tufted;
  }
  function stagesForItem(item) {
    if (item && item.kind === 'non_rug') return NON_RUG_STAGES;
    return stagesForTechnique(item && item.technique);
  }

  const STAGE_GLYPHS = {
    order_placed: '<rect x="14" y="14" width="36" height="40"/><line x1="20" y1="24" x2="44" y2="24"/><line x1="20" y1="32" x2="44" y2="32"/><line x1="20" y1="40" x2="38" y2="40"/>',
    dyeing: '<path d="M 12 20 L 14 50 C 14 52 16 54 20 54 L 44 54 C 48 54 50 52 50 50 L 52 20"/><ellipse cx="32" cy="20" rx="20" ry="4"/><path d="M 18 30 C 22 28 26 32 30 30 C 34 28 38 32 42 30 C 44 29 46 30 46 30"/><path d="M 18 40 C 22 38 26 42 30 40 C 34 38 38 42 42 40" opacity="0.5"/>',
    looming: '<rect x="12" y="10" width="40" height="44"/><line x1="18" y1="14" x2="18" y2="50"/><line x1="24" y1="14" x2="24" y2="50"/><line x1="30" y1="14" x2="30" y2="50"/><line x1="36" y1="14" x2="36" y2="50"/><line x1="42" y1="14" x2="42" y2="50"/><line x1="48" y1="14" x2="48" y2="50"/><line x1="12" y1="30" x2="52" y2="30" stroke-width="2"/>',
    tufting: '<path d="M 14 32 L 32 32 L 40 22 L 52 22 L 52 30 L 40 30 L 40 40 L 32 40 L 32 50 L 22 50 L 22 44 L 14 44 Z"/><line x1="48" y1="14" x2="48" y2="22"/><circle cx="22" cy="38" r="2" fill="currentColor" stroke="none"/>',
    knotting: '<line x1="18" y1="12" x2="18" y2="54"/><line x1="30" y1="12" x2="30" y2="54"/><line x1="42" y1="12" x2="42" y2="54"/><path d="M 12 26 C 16 26 18 24 18 20 C 18 18 20 16 24 16 L 36 16 C 40 16 42 18 42 20 C 42 24 44 26 48 26"/><path d="M 12 40 C 16 40 18 42 18 46 C 18 48 20 50 24 50 L 36 50 C 40 50 42 48 42 46 C 42 42 44 40 48 40"/>',
    weaving: '<line x1="14" y1="12" x2="14" y2="52"/><line x1="23" y1="12" x2="23" y2="52"/><line x1="32" y1="12" x2="32" y2="52"/><line x1="41" y1="12" x2="41" y2="52"/><line x1="50" y1="12" x2="50" y2="52"/><line x1="12" y1="16" x2="52" y2="16"/><line x1="12" y1="25" x2="52" y2="25"/><line x1="12" y1="34" x2="52" y2="34"/><line x1="12" y1="43" x2="52" y2="43"/><line x1="12" y1="52" x2="52" y2="52"/>',
    hand_looming: '<rect x="8" y="20" width="48" height="24"/><line x1="10" y1="23" x2="54" y2="23" opacity="0.6"/><line x1="10" y1="26" x2="54" y2="26" opacity="0.6"/><line x1="10" y1="29" x2="54" y2="29" opacity="0.6"/><line x1="10" y1="32" x2="54" y2="32" opacity="0.6"/><line x1="10" y1="35" x2="54" y2="35" opacity="0.6"/><line x1="10" y1="38" x2="54" y2="38" opacity="0.6"/><line x1="10" y1="41" x2="54" y2="41" opacity="0.6"/><line x1="16" y1="32" x2="48" y2="32" stroke-width="2.5"/><circle cx="16" cy="32" r="2" fill="currentColor" stroke="none"/><circle cx="48" cy="32" r="2" fill="currentColor" stroke="none"/>',
    shearing: '<circle cx="20" cy="44" r="6"/><circle cx="44" cy="44" r="6"/><line x1="24" y1="40" x2="48" y2="14"/><line x1="40" y1="40" x2="16" y2="14"/>',
    shearing_tufted: '<circle cx="20" cy="44" r="6"/><circle cx="44" cy="44" r="6"/><line x1="24" y1="40" x2="48" y2="14"/><line x1="40" y1="40" x2="16" y2="14"/>',
    shearing_knotted: '<circle cx="20" cy="44" r="6"/><circle cx="44" cy="44" r="6"/><line x1="24" y1="40" x2="48" y2="14"/><line x1="40" y1="40" x2="16" y2="14"/>',
    shearing_loomed: '<circle cx="20" cy="44" r="6"/><circle cx="44" cy="44" r="6"/><line x1="24" y1="40" x2="48" y2="14"/><line x1="40" y1="40" x2="16" y2="14"/>',
    binding: '<rect x="14" y="14" width="36" height="36"/><path d="M 14 18 L 50 18 M 14 22 L 50 22 M 14 46 L 50 46" opacity="0.6"/><line x1="48" y1="10" x2="36" y2="22" stroke-width="2"/><circle cx="36" cy="22" r="1.5" fill="currentColor" stroke="none"/>',
    washing: '<circle cx="32" cy="22" r="8"/><line x1="44" y1="22" x2="48" y2="22"/><line x1="16" y1="22" x2="20" y2="22"/><line x1="32" y1="10" x2="32" y2="14"/><line x1="32" y1="30" x2="32" y2="34"/><line x1="40.5" y1="13.5" x2="43.3" y2="10.7"/><line x1="20.7" y1="33.3" x2="23.5" y2="30.5"/><line x1="40.5" y1="30.5" x2="43.3" y2="33.3"/><line x1="20.7" y1="10.7" x2="23.5" y2="13.5"/><path d="M 10 44 C 16 40 22 48 28 44 C 34 40 40 48 46 44 C 50 42 54 44 54 44"/><path d="M 10 52 C 16 48 22 56 28 52 C 34 48 40 56 46 52 C 50 50 54 52 54 52" opacity="0.6"/>',
    inspection: '<circle cx="28" cy="28" r="14"/><line x1="38" y1="38" x2="50" y2="50" stroke-width="2"/><path d="M 22 28 L 26 32 L 34 24"/>',
    packing: '<ellipse cx="20" cy="32" rx="8" ry="14"/><line x1="20" y1="18" x2="48" y2="18"/><line x1="20" y1="46" x2="48" y2="46"/><ellipse cx="48" cy="32" rx="4" ry="14"/><path d="M 26 32 C 30 30 34 34 38 32" opacity="0.5"/>',
    dispatch_prep: '<path d="M 12 22 L 32 12 L 52 22 L 52 48 L 32 56 L 12 48 Z"/><line x1="32" y1="12" x2="32" y2="56" opacity="0.5"/><line x1="12" y1="22" x2="52" y2="22" opacity="0.5"/><rect x="36" y="28" width="12" height="8" opacity="0.7"/>',
    dispatched: '<path d="M 10 40 L 52 22 L 46 32 L 54 36 L 40 42 L 38 54 L 30 40 L 10 40 Z"/>',
    in_transit: '<circle cx="32" cy="32" r="16"/><ellipse cx="32" cy="32" rx="16" ry="6"/><path d="M 16 32 L 48 32" opacity="0.6"/><path d="M 12 20 L 20 18" stroke-dasharray="2 3" opacity="0.7"/><path d="M 44 46 L 52 44" stroke-dasharray="2 3" opacity="0.7"/>',
    delivered: '<path d="M 18 52 L 18 18 L 46 18 L 46 52"/><path d="M 14 52 L 50 52"/><path d="M 24 34 L 30 40 L 42 26" stroke-width="2"/>',
    default: '<circle cx="32" cy="32" r="12"/>'
  };

  function stageGlyphSvg(key) {
    const inner = STAGE_GLYPHS[key] || STAGE_GLYPHS.default;
    return '<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' + inner + '</svg>';
  }

  // Shown on the active pre-dispatch stage once the planned dispatch date
  // has passed without a shipment. Keep aligned with the dispatch-watch
  // customer note in apps/order-status-proxy/src/alerts.ts.
  const DELAY_COPY = {
    whats_happening: 'Your rug is running a little past the planned date. Each piece is finished by hand in Bhadohi and leaves only after it clears inspection. We\u2019ll email tracking the moment it ships.'
    // no did_you_know: the stage's own fact stays (Cassie, Sep 27 2026)
  };

  const LOCK_HINTS = {
    dispatched: 'Unlocks once shipped',
    in_transit: 'Unlocks once dispatched',
    delivered:  'Unlocks on arrival'
  };

  const STAGE_STORY = {
    order_placed: {
      whats_happening: 'Your order has entered the studio queue. Specifications are reviewed and New Zealand wool is pulled for your piece.',
      did_you_know: 'Every Jubi rug begins on paper. A scaled pattern is drawn on graph paper at 1:1, which the weaver reads row by row.'
    },
    dyeing: {
      whats_happening: 'The wool is weighed into batches and submerged in copper vats. The dyer adjusts temperature and time for each shade until it matches the master swatch.',
      did_you_know: 'Bhadohi\u2019s water, drawn from shallow aquifers, is naturally soft. Dyers say it binds color more evenly than municipal water and gives Bhadohi wool its characteristic warmth.'
    },
    looming: {
      whats_happening: 'The cotton warp is dressed onto an upright loom. Tension is set by hand and checked against a plumb line. This is the skeleton of the rug.',
      did_you_know: 'A single loom can be dressed in a morning or take three days, depending on the rug\u2019s knot count.'
    },
    tufting: {
      whats_happening: 'The artisan reads the pattern from behind a stretched primary-backing frame, pushing loops of yarn through with a pneumatic gun. Rows build from the bottom up.',
      did_you_know: 'A skilled tufter completes roughly one square foot per hour. The sound of a tufting room is rhythmic \u2014 a soft, percussive hum.'
    },
    knotting: {
      whats_happening: 'Each knot is tied by hand around two warp threads, then cut with a small curved blade. Rows are beaten down with a wooden comb to lock them in place.',
      did_you_know: 'A skilled knotter can tie thousands of knots a day \u2014 and a single hand-knotted rug can hold hundreds of thousands, each one tied and trimmed by hand.'
    },
    weaving: {
      whats_happening: 'Weft yarns are passed by hand between the tensioned warp threads and beaten tight with a wooden comb. Patterns emerge directly from the structure \u2014 there\u2019s no pile to hide the weave.',
      did_you_know: 'Flatweaves trace back to the earliest rug-making tradition and predate pile rugs by thousands of years. Every color change is a structural event, not a surface one.'
    },
    hand_looming: {
      whats_happening: 'On a horizontal pit loom, weft yarns are hand-fed through the stretched vertical warps, row by row. The weaver reads the pattern as they go and beats each row tight to lock it in place.',
      did_you_know: 'Pit looms are built into the floor so the weaver sits level with the cloth. The design can include subtle ribbing or carved linear details \u2014 all shaped by the weaver\u2019s hand.'
    },
    shearing_tufted: {
      whats_happening: 'The shearer carves the pile to the finished height with hand scissors. Sculpted edges and low-relief detailing are cut freehand, entirely by eye.',
      did_you_know: 'Shearers train for five to seven years before shaping a sculpted rug.'
    },
    shearing_knotted: {
      whats_happening: 'The shearer passes over the whole surface with hand shears, leveling the pile to an even height. Any stray fibers left from knotting are trimmed away.',
      did_you_know: 'Shearing reveals the pattern in full for the first time \u2014 until this step, the design is partly hidden under uneven pile.'
    },
    shearing_loomed: {
      whats_happening: 'The shearer passes over the surface with hand shears, leveling the pile to an even height. If the rug has ribbing or linear carving, these details are cut in by hand at this stage.',
      did_you_know: 'A loomed rug with hand-carved ribbing picks up the direction of the light \u2014 look at it from different angles and the pattern appears and recedes.'
    },
    binding: {
      whats_happening: 'The edges are bound in the same wool the rug is made from, finishing the perimeter and protecting the pile from wear.',
      did_you_know: 'Binders use a single needle and strong, plied wool thread. A large rug can take two full days to bind.'
    },
    washing: {
      whats_happening: 'The rug is laid in an open courtyard, soaked with well water and a mild soap, and brushed with wooden paddles. It cures in the sun over several days.',
      did_you_know: 'Sunlight is what sets the final luster in wool. Rugs washed in shade stay flatter in color.'
    },
    inspection: {
      whats_happening: 'The rug is hung on a frame and reviewed by a senior inspector from end to end. Dimensions, pile, color consistency and binding are checked against the original spec.',
      did_you_know: 'About one in twelve rugs is sent back for a small correction before it clears inspection.'
    },
    packing: {
      whats_happening: 'Your rug is wrapped in muslin and sealed in a waterproof outer for the journey. Most pieces travel rolled; extra-large sizes ship folded so they fit international freight.',
      did_you_know: 'Whether rolled or folded, a new rug may show slight creases in transit \u2014 these settle on their own within a few days of unrolling.'
    },
    dispatch_prep: {
      whats_happening: 'Customs paperwork is prepared and the carrier is booked for pickup.',
      did_you_know: 'Every Jubi piece ships DDP \u2014 you\u2019ll never be asked for duties or tariffs on arrival.'
    },
    dispatched: {
      whats_happening: 'Your rug has been picked up by the carrier and scanned into their network. It\u2019s on its way to the airport for the flight to the US.',
      did_you_know: 'Rugs travel by air freight, not sea \u2014 a piece made in India can be in your home within a week of leaving the workshop.'
    },
    in_transit: {
      whats_happening: 'Your rug is on its way. You can follow the carrier\u2019s scans below. Most carriers will notify you the day before delivery so someone can be home to receive it.',
      did_you_know: 'Air-freighted rugs typically clear US customs within 24 hours of landing and are usually delivered 5\u20137 days after dispatch.'
    },
    delivered: {
      whats_happening: 'Your rug has arrived. Unpack it, lay it flat face-up, and give it a day or two to breathe \u2014 the pile will relax and any travel creases will settle on their own.',
      did_you_know: 'A new wool rug sheds for its first few weeks. This is normal \u2014 vacuum regularly and the shedding tapers off within a month.'
    }
  };

  // Non-rug stage copy (blankets, poms, prints, samples). Shared keys
  // (dispatched, in_transit) fall back to the main STAGE_STORY.
  const NON_RUG_STAGE_STORY = {
    order_placed: {
      whats_happening: 'Your order has been received and is queued for fulfillment at the studio.',
      did_you_know: null
    },
    preparing: {
      whats_happening: 'Your order is being picked, inspected, and packaged for shipment from the studio.',
      did_you_know: null
    },
    delivered: {
      whats_happening: 'Your order has arrived. Enjoy.',
      did_you_know: null
    }
  };

  function storyForStage(item, stageKey) {
    if (item && item.kind === 'non_rug') {
      return NON_RUG_STAGE_STORY[stageKey] || STAGE_STORY[stageKey] || null;
    }
    return STAGE_STORY[stageKey] || null;
  }

  /* ---------- Date helpers ---------- */

  function addDays(d, n) {
    const out = new Date(d);
    out.setDate(out.getDate() + n);
    return out;
  }
  function fmtDate(d) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  function fmtDateLong(d) {
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }
  function fmtDateTimeShort(d) {
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  }

  /* ---------- Timeline compute ---------- */

  function computeTimeline(item, placedDate, fulfillment, now) {
    const stages = stagesForItem(item);
    let cursor = new Date(placedDate);
    const out = [];
    for (const s of stages) {
      const start = new Date(cursor);
      const end = addDays(cursor, s.days);
      cursor = end;
      out.push({ ...s, start, end, status: 'upcoming' });
    }
    const dispatchedIdx = out.findIndex((s) => s.key === 'dispatched');
    const inTransitIdx = out.findIndex((s) => s.key === 'in_transit');
    const deliveredIdx = out.findIndex((s) => s.key === 'delivered');

    // When Shopify confirms a real dispatch, rescale every stage BEFORE
    // 'dispatched' proportionally so they fit exactly into the real
    // placed→dispatched window. Keeps the timeline honest whether the rug
    // shipped on time, early, or late.
    if (fulfillment?.dispatched_at && dispatchedIdx > 0) {
      const dispatchDate = new Date(fulfillment.dispatched_at);
      const preStages = out.slice(0, dispatchedIdx);
      const nominalDays = preStages.reduce((sum, s) => sum + s.days, 0) || 1;
      const realMs = Math.max(86400000, dispatchDate - placedDate);
      const perDay = realMs / nominalDays;
      let c = new Date(placedDate);
      for (const s of preStages) {
        s.start = new Date(c);
        s.end = new Date(c.getTime() + s.days * perDay);
        c = new Date(s.end);
      }
      out[dispatchedIdx].start = dispatchDate;
      out[dispatchedIdx].end = dispatchDate;
      if (inTransitIdx >= 0) {
        out[inTransitIdx].start = dispatchDate;
        const transitDays = out[inTransitIdx].days || 5;
        if (out[inTransitIdx].end < dispatchDate) {
          out[inTransitIdx].end = addDays(dispatchDate, transitDays);
        }
      }
      if (deliveredIdx >= 0 && !fulfillment.delivered_at && inTransitIdx >= 0) {
        out[deliveredIdx].start = new Date(out[inTransitIdx].end);
        out[deliveredIdx].end = new Date(out[inTransitIdx].end);
      }
    }
    if (fulfillment?.delivered_at) {
      const d = new Date(fulfillment.delivered_at);
      if (inTransitIdx >= 0) out[inTransitIdx].end = d;
      if (deliveredIdx >= 0) { out[deliveredIdx].start = d; out[deliveredIdx].end = d; }
    }

    // Active / complete classification based on the (possibly rescaled) dates.
    let activeIdx = -1;
    for (let i = 0; i < out.length; i++) {
      const s = out[i];
      if (s.days > 0 && now >= s.start && now < s.end) { activeIdx = i; break; }
    }
    if (activeIdx < 0) {
      for (let i = 0; i < out.length; i++) {
        if (now < out[i].start) { activeIdx = i; break; }
      }
    }
    if (activeIdx < 0) activeIdx = out.length - 1;

    // Shopify fulfillment is the source of truth for dispatched+:
    //   no dispatched_at  → active cannot be dispatched / in_transit / delivered.
    //   dispatched only   → active is in_transit.
    //   + delivered_at    → active is delivered.
    if (!fulfillment?.dispatched_at) {
      if (dispatchedIdx > 0 && activeIdx >= dispatchedIdx) activeIdx = dispatchedIdx - 1;
    } else if (fulfillment.delivered_at && deliveredIdx >= 0) {
      activeIdx = deliveredIdx;
    } else if (inTransitIdx >= 0) {
      activeIdx = inTransitIdx;
    }

    // Soft "Awaiting …" label when the projected end has passed but the real
    // signal hasn't come in.
    if (!fulfillment?.dispatched_at
        && dispatchedIdx > 0
        && activeIdx === dispatchedIdx - 1
        && now > out[activeIdx].end) {
      out[activeIdx].awaitingDispatch = true;
    }
    if (fulfillment?.dispatched_at
        && !fulfillment?.delivered_at
        && inTransitIdx >= 0
        && activeIdx === inTransitIdx
        && now > out[activeIdx].end) {
      out[activeIdx].awaitingDelivery = true;
    }
    for (let i = 0; i < out.length; i++) {
      if (i < activeIdx) out[i].status = 'complete';
      else if (i === activeIdx) out[i].status = 'active';
      else out[i].status = 'upcoming';
    }
    const delivered = out.find((s) => s.key === 'delivered');
    const isDelivered = !!fulfillment?.delivered_at;
    if (isDelivered) { for (const s of out) s.status = 'complete'; }
    const estimatedArrival = delivered ? delivered.start : out[out.length - 1].start;
    const totalDays = Math.max(1, Math.round((out[out.length - 1].end - out[0].start) / 86400000));
    const elapsedDays = Math.max(0, Math.min(totalDays, Math.round((now - out[0].start) / 86400000)));
    const pct = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
    const active = out.find((s) => s.status === 'active') || null;
    return {
      stages: out,
      activeStage: active,
      isDelivered,
      hasDispatched: !!fulfillment?.dispatched_at,
      deliveredAt: isDelivered ? new Date(fulfillment.delivered_at) : null,
      estimatedArrival,
      totalDays,
      elapsedDays,
      pct,
      techniqueLabel: labelForTechnique(item.technique)
    };
  }

  /* ---------- ICS / mailto ---------- */

  function buildICS(rug, arrival, orderName) {
    const dt = (d) =>
      new Date(d).toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
    const start = new Date(arrival);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    const uid = `jubi-${encodeURIComponent(rug.id)}-${Date.now()}@jubi`;
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Jubi//Order Status//EN',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dt(new Date())}`,
      `DTSTART:${dt(start)}`,
      `DTEND:${dt(end)}`,
      `SUMMARY:Estimated arrival — ${rug.display_name}`,
      `DESCRIPTION:Your Jubi rug "${rug.display_name}" is expected to arrive (order ${orderName}).`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
  }
  function downloadICS(rug, arrival, orderName) {
    const blob = new Blob([buildICS(rug, arrival, orderName)], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jubi-${String(rug.id).replace(/[^a-z0-9]/gi, '')}-arrival.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 300);
  }
  function askAboutRug(rug, orderName) {
    const subject = `Question about order ${orderName} — ${rug.display_name}`;
    const body =
      `Hi Jubi team,\n\n` +
      `I have a question about my ${rug.display_name} (order ${orderName}):\n\n` +
      `[Your question here]\n\n` +
      `Thanks!`;
    const href = `mailto:${cfg.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  }

  /* ---------- DOM helpers ---------- */

  const qs = (sel, el) => (el || document).querySelector(sel);
  const qsa = (sel, el) => Array.from((el || document).querySelectorAll(sel));
  const text = (el, v) => { if (el) el.textContent = v ?? ''; };
  const show = (el) => el && el.removeAttribute('hidden');
  const hide = (el) => el && el.setAttribute('hidden', '');

  /* ---------- Render ---------- */

  function renderAccessStrip(mode, orderName) {
    const strip = qs('[data-access-strip]');
    if (!strip) return;
    if (mode === 'account') { hide(strip); return; }
    const label = qs('[data-access-label]', strip);
    const body = qs('[data-access-text]', strip);
    const action = qs('[data-access-action]', strip);
    if (mode === 'email') {
      text(label, 'Private link');
      text(body, 'Opened from your order confirmation. This link is private to you.');
      hide(action);
    } else if (mode === 'lookup' && orderName) {
      text(label, 'Track my order');
      text(body, `Tracking order ${orderName}. Enter a different order below if this isn't yours.`);
      show(action);
      action.onclick = () => {
        const url = new URL(location.href);
        url.search = '';
        location.href = url.toString();
      };
    } else {
      hide(strip); return;
    }
    show(strip);
  }

  function renderHero(order, timelines) {
    const hero = qs('[data-hero]');
    const firstName = cfg.customer?.firstName || order.customer_first_name || null;
    const isCancelled = !!order.cancelled_at;
    const anyDelivered = timelines.every((t) => t.isDelivered);
    const anyInTransit = !anyDelivered && timelines.some((t) => t.hasDispatched);
    const n = order.items.length;
    const allNonRug = order.items.every((it) => it.kind === 'non_rug');

    hero.dataset.cancelled = isCancelled ? 'true' : 'false';

    const placedLong = fmtDateLong(new Date(order.created_at));
    text(qs('[data-hero-eyebrow]', hero), `Order ${order.order_name} · Placed ${placedLong}`);
    const greetingEl = qs('[data-hero-greeting]', hero);
    if (firstName) {
      text(greetingEl, `For ${firstName}`);
      show(greetingEl);
    } else {
      hide(greetingEl);
    }

    if (isCancelled) {
      const cancelledLong = fmtDateLong(new Date(order.cancelled_at));
      const support = cfg.supportEmail || 'hello@shop-jubi.com';
      text(
        qs('[data-hero-subhead]', hero),
        `This order was cancelled on ${cancelledLong}. If you have questions, reach out to ${support}.`
      );
      show(hero);
      return;
    }
    const rugSubject = n === 1 ? 'Your rug' : 'Your order';
    const genericSubject = n === 1 ? 'Your order' : 'Your order';
    const subject = allNonRug ? genericSubject : rugSubject;
    const subhead = anyDelivered
      ? (n === 1
          ? `${subject} is home. Below, the full journey — stage by stage.`
          : `${subject} is home. Below, the full journey for each piece.`)
      : anyInTransit
        ? (n === 1
            ? `${subject} is on its way to you. Follow it through the last leg of its journey.`
            : `${subject} is on its way to you. Follow each piece through the last leg of its journey.`)
        : allNonRug
          ? (n === 1
              ? `${subject} is being prepared for dispatch. We'll update this page the moment it ships.`
              : `${subject} is being prepared for dispatch. We'll update this page the moment each piece ships.`)
          : (n === 1
              ? 'Your rug is in production in Bhadohi. Follow it through the atelier, stage by stage.'
              : 'Your order is in production in Bhadohi. Follow each piece through the atelier, stage by stage.');
    text(qs('[data-hero-subhead]', hero), subhead);

    text(qs('[data-stat-pieces]', hero), String(n));
    const ship = [order.ship_city, order.ship_region].filter(Boolean).join(', ') || '—';
    text(qs('[data-stat-ship]', hero), ship);

    if (anyDelivered) {
      text(qs('[data-stat-arrival-label]', hero), 'Delivered');
      const latest = timelines.reduce((acc, t) => (t.deliveredAt && (!acc || t.deliveredAt > acc) ? t.deliveredAt : acc), null);
      text(qs('[data-stat-arrival]', hero), latest ? fmtDateLong(latest) : '—');
      text(qs('[data-stat-arrival-hint]', hero), 'Order complete');
      text(qs('[data-stat-journey-label]', hero), 'Total journey');
      const total = Math.max(...timelines.map((t) => t.totalDays));
      text(qs('[data-stat-journey]', hero), `${total} days`);
      text(qs('[data-stat-journey-hint]', hero), 'From order to doorstep');
    } else {
      text(qs('[data-stat-arrival-label]', hero), 'Estimated arrival');
      const range = timelines.map((t) => t.estimatedArrival).sort((a, b) => a - b);
      const text1 = range.length === 1
        ? fmtDateLong(range[0])
        : `${fmtDate(range[0])} – ${fmtDate(range[range.length - 1])}`;
      text(qs('[data-stat-arrival]', hero), text1);
      text(qs('[data-stat-arrival-hint]', hero), allNonRug ? 'Ships from the studio' : 'Air freight · Duties included');
      text(qs('[data-stat-journey-label]', hero), 'Longest lead');
      const total = Math.max(...timelines.map((t) => t.totalDays));
      const leadText = allNonRug ? `${total} days` : `${Math.round(total / 7)} weeks`;
      text(qs('[data-stat-journey]', hero), leadText);
      text(qs('[data-stat-journey-hint]', hero), 'From order to delivery');
    }
    show(hero);
  }

  function renderArtisan(chip, fallback, artisan) {
    if (!artisan) {
      hide(chip);
      show(fallback);
      return;
    }
    hide(fallback);
    const avatar = qs('[data-artisan-avatar]', chip);
    const portrait = qs('[data-artisan-portrait]', chip);
    const initials = qs('[data-artisan-initials]', chip);
    if (artisan.portrait_url) {
      portrait.src = artisan.portrait_url;
      portrait.alt = artisan.name;
      show(portrait);
      hide(initials);
    } else {
      hide(portrait);
      show(initials);
      text(initials, artisan.initials);
    }
    text(qs('[data-artisan-name]', chip), artisan.name);
    const meta = [artisan.role, artisan.years_with_jubi ? `${artisan.years_with_jubi} years` : null, artisan.village].filter(Boolean).join(' · ');
    text(qs('[data-artisan-meta]', chip), meta);
    show(chip);
  }

  function renderTrackingChip(container, fulfillment, timeline) {
    container.innerHTML = '';
    if (!fulfillment || (!fulfillment.tracking_number && !fulfillment.delivered_at)) {
      hide(container);
      return;
    }
    show(container);
    if (timeline.isDelivered) {
      const delivered = timeline.deliveredAt;
      container.innerHTML =
        `<dl><div class="jubi-os__tracking-row"><dt>Delivered</dt>` +
        `<dd>${fmtDateLong(delivered)}</dd></div></dl>` +
        `<div class="jubi-os__tracking-scan">${(fulfillment.carrier || 'Carrier')}` +
        (fulfillment.tracking_number ? ` · ${fulfillment.tracking_number}` : '') +
        `</div>`;
      return;
    }
    const carrier = fulfillment.carrier || 'Carrier';
    const href = fulfillment.tracking_url || '#';
    const numEsc = (fulfillment.tracking_number || '').replace(/</g, '&lt;');
    container.innerHTML =
      `<dl>` +
      `<div class="jubi-os__tracking-row"><dt>Carrier</dt>` +
      `<dd>${carrier}${fulfillment.tracking_url ? ` · <a href="${href}" target="_blank" rel="noopener">Track with ${carrier} →</a>` : ''}</dd></div>` +
      (numEsc ? `<div class="jubi-os__tracking-row"><dt>Tracking</dt>` +
        `<dd><span class="jubi-os__tracking-number">${numEsc}</span>` +
        `<button type="button" class="jubi-os__tracking-copy" data-copy="${numEsc.replace(/\s/g, '')}">Copy</button></dd></div>` : '') +
      `</dl>` +
      (fulfillment.latest_event ? `<div class="jubi-os__tracking-scan">` +
        `${fulfillment.latest_event.status}` +
        (fulfillment.latest_event.location ? ` — ${fulfillment.latest_event.location}` : '') +
        ` · ${fmtDateTimeShort(new Date(fulfillment.latest_event.happened_at))}` +
        `</div>` : '');
    const copyBtn = qs('[data-copy]', container);
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        navigator.clipboard?.writeText(copyBtn.dataset.copy).catch(() => {});
        const orig = copyBtn.textContent;
        copyBtn.textContent = 'Copied';
        setTimeout(() => (copyBtn.textContent = orig), 1500);
      });
    }
  }

  function renderTimeline(mount, item, timeline, fulfillment) {
    const rowTpl = qs('#jubi-os-timeline-row-template');
    mount.innerHTML = '';
    const rows = [];
    const compact = item && item.kind === 'non_rug';

    timeline.stages.forEach((stage) => {
      const li = rowTpl.content.firstElementChild.cloneNode(true);
      li.dataset.status = stage.status;
      const name = qs('[data-stage-name]', li);
      const statusline = qs('[data-stage-statusline]', li);
      const head = qs('[data-stage-head]', li);
      const lockhint = qs('[data-stage-lockhint]', li);

      text(name, stage.name);

      if (compact) {
        // Non-rug: hide projected dates entirely (they're not meaningful for
        // stock/short-production items — shows "Expected · Mar 29" when
        // today is already past). Show only the state.
        const compactLabel = stage.awaitingDispatch
          ? 'Awaiting dispatch'
          : stage.awaitingDelivery
            ? 'Awaiting delivery'
            : stage.status === 'active'
              ? 'Happening now'
              : stage.status === 'complete'
                ? 'Completed'
                : 'Upcoming';
        text(statusline, compactLabel);
        li.dataset.compact = 'true';
        mount.appendChild(li);
        return;
      }

      const prefix = stage.status === 'active' ? 'Happening now · '
        : stage.status === 'complete' ? 'Completed · '
        : 'Expected · ';
      const dateLabel = stage.awaitingDispatch
        ? 'Awaiting dispatch'
        : stage.awaitingDelivery
          ? 'Awaiting delivery'
          : stage.days === 0
            ? fmtDate(stage.start)
            : `${fmtDate(stage.start)} – ${fmtDate(stage.end)}`;
      text(statusline, prefix + dateLabel);

      const whats = qs('[data-stage-whats]', li);
      const fact = qs('[data-stage-fact]', li);
      const detail = qs('[data-stage-detail]', li);
      const tracking = qs('[data-stage-tracking]', li);
      const glyph = qs('[data-stage-glyph]', li);

      const story = storyForStage(item, stage.key);
      if (story) {
        text(whats, story.whats_happening);
        text(fact, story.did_you_know);
      }
      // Past the planned dispatch date with nothing shipped: say so plainly
      // instead of leaving a stalled stage with no explanation.
      if (stage.awaitingDispatch) {
        text(whats, DELAY_COPY.whats_happening);
      }
      const didYouKnow = qs('.jubi-os__stage-didyouknow', li);
      if (didYouKnow) {
        if (story && story.did_you_know) show(didYouKnow);
        else hide(didYouKnow);
      }
      if (glyph) glyph.innerHTML = stageGlyphSvg(stage.key);

      // Lock future shipping stages until they're actually in-flight.
      const shipLocked = stage.status === 'upcoming' && LOCK_HINTS[stage.key];
      if (shipLocked) {
        li.dataset.locked = 'true';
        text(lockhint, LOCK_HINTS[stage.key]);
        show(lockhint);
      }

      // Tracking chip only on in_transit + delivered stages.
      if ((stage.key === 'in_transit' || stage.key === 'delivered') && !shipLocked) {
        renderTrackingChip(tracking, fulfillment, timeline);
      }

      // Expand/collapse — active stage starts expanded. Only one row open at a time.
      detail.removeAttribute('hidden');
      const setExpanded = (open) => {
        li.dataset.expanded = open ? 'true' : 'false';
        head.setAttribute('aria-expanded', open ? 'true' : 'false');
      };
      const collapseOthers = () => {
        rows.forEach((r) => { if (r.li !== li) r.setExpanded(false); });
      };
      rows.push({ li, setExpanded });

      setExpanded(stage.status === 'active');
      head.addEventListener('click', () => {
        if (li.dataset.locked === 'true') return;
        const opening = li.dataset.expanded !== 'true';
        if (opening) collapseOthers();
        setExpanded(opening);
      });

      mount.appendChild(li);
    });
  }

  function renderRug(order, item, idx, now) {
    const tpl = qs('#jubi-os-rug-template');
    const node = tpl.content.firstElementChild.cloneNode(true);
    const isCancelled = !!order.cancelled_at;
    const isRefunded = !!item.refunded;
    if (isCancelled) node.dataset.cancelled = 'true';
    if (isRefunded) node.dataset.refunded = 'true';
    const timeline = computeTimeline(item, new Date(order.created_at), item.fulfillment, now);

    // Image — hide the whole block when there's nothing to show rather
    // than displaying a "Studio photograph" placeholder.
    const imageWrap = qs('[data-rug-image]', node);
    const img = qs('[data-rug-img]', node);
    imageWrap.dataset.fit = item.image_fit;
    if (item.image_url) {
      img.src = item.image_url;
      img.alt = item.display_name;
    } else {
      hide(imageWrap);
    }

    const pieceLabel = `Piece ${idx + 1} of ${order.items.length}`;
    text(qs('[data-rug-piece]', node), isRefunded ? `${pieceLabel} · Refunded` : pieceLabel);
    text(qs('[data-rug-name]', node), item.display_name);

    const isNonRug = item.kind === 'non_rug';
    const specsHead = isNonRug
      ? [item.size].filter(Boolean).join(' · ')
      : [item.size, timeline.techniqueLabel].filter(Boolean).join(' · ');
    qs('[data-rug-specs]', node).innerHTML = isNonRug
      ? specsHead
      : `${specsHead}<br>100% New Zealand wool`;

    // Cancelled or refunded: skip design block, facts, actions, artisan,
    // timeline. CSS hides the right column and everything else.
    if (isCancelled || isRefunded) return node;

    // Custom-product "Your design" block
    if (item.is_custom && item.custom_selections.length > 0) {
      const block = qs('[data-design-block]', node);
      const list = qs('[data-design-list]', node);
      list.innerHTML = '';
      item.custom_selections.forEach((s) => {
        const row = document.createElement('div');
        row.className = 'jubi-os__design-row';
        row.innerHTML =
          `<dt>${s.label.replace(/</g, '&lt;')}</dt>` +
          `<dd class="jubi-os__design-value">${s.color_name.replace(/</g, '&lt;')}</dd>`;
        list.appendChild(row);
      });
      show(block);
    }

    // Facts (varies by state)
    const facts = qs('[data-facts]', node);
    facts.innerHTML = '';
    const makeFact = (dt, dd, hint) => {
      const wrap = document.createElement('div');
      wrap.className = 'jubi-os__fact';
      wrap.innerHTML = `<dt>${dt}</dt><dd>${dd}${hint ? `<div class="jubi-os__fact-hint">${hint}</div>` : ''}</dd>`;
      return wrap;
    };
    if (timeline.isDelivered) {
      facts.appendChild(makeFact('Status', 'Delivered'));
      if (!isNonRug) facts.appendChild(makeFact('Journey', `${timeline.totalDays} days`));
      if (timeline.deliveredAt) facts.appendChild(makeFact('Delivered', fmtDateLong(timeline.deliveredAt)));
    } else if (isNonRug) {
      // Non-rug: projected dates/progress aren't reliable. Just surface
      // the current state.
      facts.appendChild(makeFact('Status', timeline.activeStage?.name || '—'));
    } else {
      facts.appendChild(makeFact('Now', timeline.activeStage?.name || '—'));
      facts.appendChild(makeFact('Progress', `${timeline.pct}% · day ${timeline.elapsedDays} of ${timeline.totalDays}`));
      facts.appendChild(makeFact('Estimated arrival', fmtDateLong(timeline.estimatedArrival), 'Final date confirmed at dispatch'));
    }

    // Actions — hide ICS for non-rug (projected arrival isn't reliable) and
    // once delivered.
    const actions = qs('[data-actions]', node);
    const btnIcs = qs('[data-action-ics]', actions);
    const btnMail = qs('[data-action-mailto]', actions);
    if (timeline.isDelivered || isNonRug) { btnIcs.remove(); }
    else btnIcs.addEventListener('click', () => downloadICS(item, timeline.estimatedArrival, order.order_name));
    if (isNonRug) {
      btnMail.innerHTML = 'Ask about this item <span aria-hidden="true">→</span>';
    }
    btnMail.addEventListener('click', () => askAboutRug(item, order.order_name));

    // Artisan (only for rugs — non-rugs aren't made in the Bhadohi atelier)
    const chip = qs('[data-artisan]', node);
    const fallback = qs('[data-artisan-fallback]', node);
    if (isNonRug) {
      hide(chip);
      hide(fallback);
    } else {
      renderArtisan(chip, fallback, item.artisan);
    }

    // Timeline
    const atelierLabel = qs('[data-atelier-label]', node);
    const doneLabel = timeline.isDelivered ? 'The journey' : null;
    text(atelierLabel, doneLabel || (isNonRug ? 'Your order' : 'In the atelier'));

    // Non-rug items don't have per-stage dropdowns. If there's tracking,
    // show a standalone chip between the label and the timeline.
    if (isNonRug && item.fulfillment && (item.fulfillment.tracking_number || item.fulfillment.delivered_at)) {
      const timelineEl = qs('[data-timeline]', node);
      const standalone = document.createElement('div');
      standalone.className = 'jubi-os__standalone-tracking';
      timelineEl.parentNode.insertBefore(standalone, timelineEl);
      renderTrackingChip(standalone, item.fulfillment, timeline);
    }

    renderTimeline(qs('[data-timeline]', node), item, timeline, item.fulfillment);

    return node;
  }

  function renderOrder(order) {
    const now = new Date();
    const timelines = order.items.map((it) => computeTimeline(it, new Date(order.created_at), it.fulfillment, now));

    renderHero(order, timelines);

    // Breadcrumb update
    const crumb = qs('[data-crumb-order]');
    if (crumb) text(crumb, `Order ${order.order_name}`);

    const rugsMount = qs('[data-rugs]');
    rugsMount.innerHTML = '';
    order.items.forEach((it, idx) => {
      rugsMount.appendChild(renderRug(order, it, idx, now));
    });
  }

  /* ---------- Proxy fetch ---------- */

  async function fetchOrder(params) {
    const url = cfg.proxyPath + '/order?' + new URLSearchParams(params).toString();
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw Object.assign(new Error('proxy_error'), { status: res.status, body });
    }
    return (await res.json()).order;
  }
  async function lookupOrder({ order, email }) {
    const res = await fetch(cfg.proxyPath + '/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order, email }),
      credentials: 'same-origin'
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw Object.assign(new Error('lookup_failed'), { status: res.status, body });
    }
    return (await res.json()).order;
  }

  /* ---------- Boot ---------- */

  function paramsFromUrl() {
    const sp = new URLSearchParams(location.search);
    return { order: sp.get('order'), key: sp.get('key'), email: sp.get('email') };
  }

  function showError(message) {
    const err = qs('[data-error]');
    const text = qs('[data-error-text]', err);
    if (message) text.innerHTML = message;
    show(err);
    hide(qs('[data-loading]'));
  }

  async function boot() {
    const { order: orderParam, key, email: emailParam } = paramsFromUrl();

    // Wire up lookup form regardless — used when no order is in URL, or when
    // user comes back after landing on an error state.
    const form = qs('[data-lookup-form]');
    const formSection = qs('[data-lookup]');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(form);
        const submitBtn = qs('[data-lookup-submit]', form);
        const errEl = qs('[data-lookup-error]', form);
        hide(errEl);
        submitBtn.disabled = true;
        try {
          const order = await lookupOrder({ order: fd.get('order'), email: fd.get('email') });
          hide(formSection);
          hide(qs('[data-error]'));
          renderAccessStrip('lookup', order.order_name);
          renderOrder(order);
          // Update URL so refresh works
          const url = new URL(location.href);
          url.searchParams.set('order', order.order_name.replace(/^#/, ''));
          history.replaceState(null, '', url.toString());
        } catch (err) {
          errEl.textContent = "We couldn't find an order matching that number and email. Double-check both and try again.";
          show(errEl);
        } finally {
          submitBtn.disabled = false;
        }
      });
    }

    if (!orderParam) {
      // No order in the URL — show the lookup form for everyone, including
      // logged-in customers (they may want to track a gift or someone else's
      // order).
      show(formSection);
      return;
    }

    show(qs('[data-loading]'));
    try {
      let order;
      if (emailParam) {
        order = await lookupOrder({ order: orderParam, email: emailParam });
      } else {
        const params = { order: orderParam };
        if (key) params.key = key;
        order = await fetchOrder(params);
      }
      renderAccessStrip((emailParam || key) ? 'email' : (cfg.customer ? 'account' : 'lookup'), order.order_name);
      renderOrder(order);
      hide(qs('[data-loading]'));
    } catch (err) {
      hide(qs('[data-loading]'));
      if (err.status === 404) {
        showError("We couldn't find that order. If you followed a link from an email, the link may have expired. Use the form below to look it up manually.");
        show(formSection);
      } else if (err.status === 401) {
        showError('This order requires a valid link. Check the tracking link in your order confirmation email, or <a href="' + cfg.accountLink + '">sign in</a>.');
        show(formSection);
      } else {
        showError('Something went wrong loading your order. Please try again in a moment.');
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
